import { log } from "../../shared/services/debug-logger";
import { api, apiUrl } from "../../shared/services/service";
import { isHoldCleaningMockEnabled } from "../mocks/hold-cleaning-mock-mode";
import {
  mockGetHoldCleaningAssessment,
  mockGenerateHoldCleaningAssessment,
  mockGetHoldCleaningCrew,
  mockGetHoldCleaningImageContent,
  mockGetHoldCleaningImages,
  mockGetHoldCleaningRefreshRun,
  mockGetHoldCleaningRefreshRequest,
  mockGetHoldCleaningRefreshStatus,
  mockGetHoldCleaningEmailIntelligenceStatus,
  mockGetHoldCleaningVoyage,
  mockListHoldCleaningRefreshRuns,
  mockListHoldCleaningVoyages,
  mockRequestHoldCleaningRefresh,
  mockRequestHoldCleaningEmailIntelligenceBackfill,
  mockRetryHoldCleaningEmailIntelligenceFailures,
  mockRetryHoldCleaningRefreshRun,
  mockSaveHoldCleaningAssessment,
  mockSourceHealth,
} from "../mocks/hold-cleaning.mock";
import type {
  HoldCleaningAiFeedbackPayload,
  HoldCleaningReport,
  HoldCleaningAssessment,
  HoldCleaningAssessmentPayload,
  HoldCleaningAssessmentResult,
  HoldCleaningCrewSummary,
  HoldCleaningDatasetFreshness,
  HoldCleaningEmailIntelligenceStatus,
  HoldCleaningFormField,
  HoldCleaningImage,
  HoldCleaningImageSource,
  HoldCleaningRefreshRequest,
  HoldCleaningRefreshStatus,
  HoldCleaningRefreshRun,
  HoldCleaningRunPage,
  HoldCleaningSource,
  HoldCleaningSourceHealth,
  HoldCleaningCurrentCleaning,
  HoldCleaningDesignStatus,
  HoldCleaningHistoryProjection,
  HoldCleaningLatestRating,
  HoldCleaningManualEntryInput,
  HoldCleaningVesselFacts,
  HoldCleaningVoyageDetail,
  HoldCleaningVoyagePage,
  HoldCleaningVoyageQuery,
  HoldCleaningVoyageSummary,
  HoldCleaningWarning,
} from "../models/hold-cleaning.models";
import axios from "axios";

const HOLD_CLEANING_API = `${apiUrl}/hold-cleaning`;
const HOLD_CLEANING_READ_TIMEOUT_MS = 20_000;
const AI_PROMPT_CONTRACT_VERSION = "hold-cleaning-assessment-v1";

type ApiSource =
  | "SNOWFLAKE"
  | "SHIPFIX"
  | "OUTLOOK"
  | "EMAIL_INTELLIGENCE"
  | "IMAGE_CLASSIFICATION"
  | "ZERONORTH"
  | "NOAA"
  | "OPSLINK_CREW_REPORT"
  | "LEGACY_SHAREPOINT_CREW"
  | "SHAREPOINT_OT_OPS_CREW_ON_BOARD";
type ApiImageSource = "SHIPFIX" | "OUTLOOK" | "MANUAL";
type ApiHoldNumberEvidenceSource =
  | "STRUCTURED_METADATA"
  | "WORKSHEET_NAME"
  | "DRAWING_ANCHOR"
  | "PDF_PAGE_LABEL"
  | "DOCUMENT_CAPTION"
  | "ARCHIVE_PATH"
  | "FILENAME"
  | "APPROVED_MESSAGE_TEXT";
type ApiRefreshRunStatus =
  | "CLAIMED"
  | "RUNNING"
  | "VALIDATING"
  | "VALIDATED"
  | "ACTIVATING"
  | "SUCCEEDED"
  | "FAILED_RETRYABLE"
  | "FAILED_FINAL"
  | "STALE"
  | "CANCELLED";
type ApiRefreshTrigger = "SCHEDULED" | "MANUAL" | "RETRY" | "BACKFILL" | "CLEANUP";

interface ApiWarning {
  source: ApiSource;
  code: string;
  message: string;
  retryable: boolean;
}

interface ApiMeta {
  contractVersion: string;
  datasetId?: string;
  generatedAt: string;
  warnings: ApiWarning[];
  nextCursor?: string | null;
}

interface ApiEnvelope<T> {
  data: T;
  meta: ApiMeta;
}

interface ApiVoyageSummary {
  photoCount?: number;
  dataCoverage?: "NONE" | "PARTIAL" | "FULL";
  voyageId: string;
  imo: string;
  vesselCode: string;
  voyageNo: string;
  vesselName: string;
  voyageStatus: string;
  voyageStartAt: string | null;
  voyageEndAt: string | null;
  completionAt: string | null;
  loadPort?: string | null;
  dischargePort?: string | null;
  currentCargo: string | null;
  previousCargo: string | null;
  nextCargo?: string | null;
  nextLoadPort?: string | null;
  nextEta?: string | null;
  laycanStartAt?: string | null;
  laycanEndAt?: string | null;
  cleaningStatus?: HoldCleaningDesignStatus;
  latestRating?: Omit<HoldCleaningLatestRating, "voyageNumber"> & { voyageNo: string | null };
  numberOfHolds: number | null;
  scopeReason: string;
  sourceSnapshotId: string;
  sourceLastUpdatedAt: string;
  warnings: ApiWarning[];
}

interface ApiCrewReportSummary {
  source: "OPSLINK_CREW_REPORT";
  imo: string;
  reportReceivedAt: string | null;
  crewCount: number;
  validationStatus: "VALID" | "STALE" | "MISSING";
  stale: boolean;
  rankCounts: Array<{ designationRank: string; count: number }>;
}

interface ApiLegacyCrewSummary {
  source: "SHAREPOINT_OT_OPS_CREW_ON_BOARD";
  imo: string;
  workbookModifiedAt: string;
  lastSuccessfulImportAt: string;
  lineageId: string;
  crewCount: number;
  validationStatus: "VALID" | "STALE" | "MISSING";
  stale: boolean;
  duplicateSourceWarning: boolean;
  latestRejectedItemWarning: boolean;
  rankCounts: Array<{ designationRank: string; count: number }>;
}

type ApiCrewSummary = ApiCrewReportSummary | ApiLegacyCrewSummary;

interface ApiVoyageDetail extends ApiVoyageSummary {
  charterContract: string | null;
  loadPort: string | null;
  dischargePort: string | null;
  formSummary: Record<string, unknown>;
  crewSummary: ApiCrewSummary | null;
  vesselFacts?: HoldCleaningVesselFacts;
  currentCleaning?: HoldCleaningCurrentCleaning;
  history?: HoldCleaningHistoryProjection;
  datasetId: string;
}

interface ApiImage {
  category?: HoldCleaningImage["category"];
  categoryMode?: "SHADOW";
  imageId: string;
  voyageId: string;
  source: ApiImageSource;
  holdNumber: number | null;
  holdConfidence: number | null;
  evidenceSource: ApiHoldNumberEvidenceSource | null;
  mediaType: string;
  byteSize: number;
  width: number | null;
  height: number | null;
  createdAt: string;
  caption: string | null;
}

interface ApiSourceHealth {
  source: ApiSource;
  status:
    | "NOT_STARTED"
    | "RUNNING"
    | "SUCCEEDED"
    | "PARTIAL"
    | "FAILED_RETRYABLE"
    | "FAILED_FINAL"
    | "SKIPPED_NOT_APPLICABLE";
  enabled: boolean;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  sourceUpdatedAt: string | null;
  stale: boolean;
  checkpointLagSeconds: number | null;
  warningCount: number;
  lastErrorCode: string | null;
}

interface ApiRefreshRun {
  runId: string;
  requestId: string;
  trigger: ApiRefreshTrigger;
  requestedAt: string;
  requestedBy: string;
  datasetId: string | null;
  status: ApiRefreshRunStatus;
  startedAt: string | null;
  completedAt: string | null;
  heartbeatAt: string | null;
  sourceStatuses: ApiSourceHealth[];
  validationSummary: Record<string, unknown>;
  errorCode: string | null;
  errorDetail: string | null;
}

interface ApiRefreshStatus {
  pendingCount: number;
  activeRun: HoldCleaningRefreshStatus["activeRun"];
  latestSuccess: ApiRefreshRun | null;
  latestFailure: ApiRefreshRun | null;
  schedule: HoldCleaningRefreshStatus["schedule"];
}

const encodePath = (value: string) => encodeURIComponent(value);

const notImplementedStatus: HoldCleaningDesignStatus = {
  code: "NO_CLEANING_UPDATE",
  label: "No cleaning update",
  detail: "No cleaning update is available for this voyage.",
  implementationStatus: "PARTIAL",
};

const notImplementedRating: HoldCleaningLatestRating = {
  score: null,
  maximumScore: 3,
  label: "—",
  voyageNumber: null,
  implementationStatus: "NOT_IMPLEMENTED",
};

const sourceMap: Record<ApiSource, HoldCleaningSource> = {
  SNOWFLAKE: "snowflake",
  SHIPFIX: "shipfix",
  OUTLOOK: "outlook",
  EMAIL_INTELLIGENCE: "email-intelligence",
  IMAGE_CLASSIFICATION: "image-classification",
  ZERONORTH: "zeronorth",
  NOAA: "noaa",
  OPSLINK_CREW_REPORT: "crew-report",
  LEGACY_SHAREPOINT_CREW: "legacy-sharepoint",
  SHAREPOINT_OT_OPS_CREW_ON_BOARD: "legacy-sharepoint",
};

const createIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `hold-cleaning-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const toWarning = (warning: ApiWarning): HoldCleaningWarning => ({
  code: warning.code,
  message: warning.message,
  source: sourceMap[warning.source],
});

const toDataset = (meta: ApiMeta): HoldCleaningDatasetFreshness => ({
  datasetId: meta.datasetId ?? "unavailable",
  status: "ACTIVE",
  activatedAt: meta.generatedAt,
  collectedAt: meta.generatedAt,
  isPartial: meta.warnings.length > 0,
  warnings: meta.warnings.map(toWarning),
});

const warningKey = (warning: ApiWarning) => `${warning.source}:${warning.code}:${warning.message}`;

const uniqueWarnings = (warnings: ApiWarning[]): HoldCleaningWarning[] =>
  Array.from(new Map(warnings.map((warning) => [warningKey(warning), toWarning(warning)])).values());

const toVoyageSummary = (voyage: ApiVoyageSummary, datasetId?: string): HoldCleaningVoyageSummary => ({
  imageCount: voyage.photoCount,
  voyageId: voyage.voyageId,
  sourceVoyageKey: `${voyage.vesselCode}${voyage.voyageNo}`,
  imo: voyage.imo,
  vesselName: voyage.vesselName,
  vesselCode: voyage.vesselCode,
  voyageNumber: voyage.voyageNo,
  status: voyage.voyageStatus,
  startAt: voyage.voyageStartAt,
  completedAt: voyage.completionAt ?? voyage.voyageEndAt,
  loadPort: voyage.loadPort,
  dischargePort: voyage.dischargePort,
  cargo: voyage.currentCargo,
  previousCargo: voyage.previousCargo,
  nextCargo: voyage.nextCargo,
  nextLoadPort: voyage.nextLoadPort,
  nextEta: voyage.nextEta,
  laycanStartAt: voyage.laycanStartAt,
  laycanEndAt: voyage.laycanEndAt,
  cleaningStatus: voyage.cleaningStatus ?? notImplementedStatus,
  latestRating: voyage.latestRating
    ? { ...voyage.latestRating, voyageNumber: voyage.latestRating.voyageNo }
    : notImplementedRating,
  numberOfHolds: voyage.numberOfHolds,
  scopeReason: voyage.scopeReason,
  sourceUpdatedAt: voyage.sourceLastUpdatedAt,
  activeDatasetId: datasetId,
});

const displayValue = (value: unknown) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  return JSON.stringify(value);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const optionalText = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const humanize = (value: string): string =>
  value
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/^./, (character) => character.toUpperCase());

const recordToFields = (record: Record<string, unknown>): HoldCleaningFormField[] =>
  Object.entries(record).map(([label, value]) => ({
    label: label.replace(/[_-]+/g, " "),
    value: displayValue(value),
  }));

const toForms = (
  voyageId: string,
  formSummary: Record<string, unknown>,
  sourceLastUpdatedAt: string,
): HoldCleaningVoyageDetail["forms"] => {
  const formRecords = Array.isArray(formSummary.forms) ? formSummary.forms.filter(isRecord) : [];
  if (formRecords.length === 0) {
    return Object.keys(formSummary).length === 0
      ? []
      : [
          {
            formId: `${voyageId}-summary`,
            templateName: "Normalized Hold Cleaning form",
            status: "AVAILABLE",
            updatedAt: sourceLastUpdatedAt,
            sections: [{ title: "Form summary", fields: recordToFields(formSummary) }],
          },
        ];
  }

  return formRecords.map((form, index) => {
    const normalizedFields = isRecord(form.normalized_json) ? recordToFields(form.normalized_json) : [];
    const detailFields = recordToFields(
      Object.fromEntries(Object.entries(form).filter(([key]) => key !== "normalized_json")),
    );
    const formType = optionalText(form.form_type) ?? `Form ${index + 1}`;
    const submittedAt = optionalText(form.submitted_at);

    return {
      formId: `${voyageId}-${formType.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${index + 1}`,
      templateName: `${humanize(formType)} form`,
      status: optionalText(form.parse_status) ?? optionalText(form.result_status) ?? "AVAILABLE",
      submittedAt,
      updatedAt: submittedAt ?? sourceLastUpdatedAt,
      sections: [
        { title: "Form details", fields: detailFields },
        ...(normalizedFields.length > 0 ? [{ title: "Normalized fields", fields: normalizedFields }] : []),
      ],
    };
  });
};

const crewWarnings = (summary: ApiCrewSummary): HoldCleaningWarning[] => {
  const warnings: HoldCleaningWarning[] = [];
  if (summary.stale) {
    warnings.push({
      code: "CREW_SOURCE_STALE",
      message: "The current OpsLink Crew Report is stale.",
      source: summary.source === "OPSLINK_CREW_REPORT" ? "crew-report" : "legacy-sharepoint",
    });
  }
  if (summary.source !== "OPSLINK_CREW_REPORT" && summary.duplicateSourceWarning) {
    warnings.push({
      code: "CREW_DUPLICATE_SOURCE",
      message: "More than one candidate crew workbook was found.",
      source: "legacy-sharepoint",
    });
  }
  if (summary.source !== "OPSLINK_CREW_REPORT" && summary.latestRejectedItemWarning) {
    warnings.push({
      code: "CREW_LATEST_REJECTED",
      message: "The latest crew workbook candidate was rejected.",
      source: "legacy-sharepoint",
    });
  }
  return warnings;
};

const toCrewSummary = (summary: ApiCrewSummary): HoldCleaningCrewSummary => ({
  totalCrew: summary.crewCount,
  rankCounts: summary.rankCounts,
  sourceUpdatedAt: summary.source === "OPSLINK_CREW_REPORT" ? summary.reportReceivedAt : summary.lastSuccessfulImportAt,
  warnings: crewWarnings(summary),
});

const readEtag = (headers: Record<string, unknown>) => {
  const value = headers.etag ?? headers.ETag;
  if (typeof value !== "string" || (value !== "*" && !/^W\/"[a-f0-9-]+:\d+"$/i.test(value))) {
    throw new Error("Assessment response did not include a valid ETag. Reload the assessment before editing.");
  }
  return value;
};

export const getHoldCleaningErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    if (error.code === "ECONNABORTED") {
      return "The Hold Cleaning request timed out. Please retry after the database recovers.";
    }
    const responseData = error.response?.data as
      | {
          error?:
            | {
                code?: string;
                message?: string;
                retryable?: boolean;
                details?: { requestId?: string | null; runId?: string | null; status?: string | null };
              }
            | string;
          message?: string;
        }
      | string
      | undefined;
    if (error.response?.status === 412) {
      return "This assessment changed after you opened it. Reload it before saving your edits.";
    }
    if (error.response?.status === 403) return "You do not have permission to perform this action.";
    if (typeof responseData === "string") return responseData;
    if (typeof responseData?.error === "string") return responseData.error;
    if (error.response?.status === 409 && responseData?.error?.details) {
      const details = responseData.error.details;
      return `${responseData.error.message ?? "Another refresh is active."} Active request ${details.requestId ?? "unknown"}, run ${details.runId ?? "not claimed"}, status ${details.status ?? "unknown"}.`;
    }
    return responseData?.error?.message ?? responseData?.message ?? error.message;
  }
  return error instanceof Error ? error.message : "The Hold Cleaning request failed.";
};

export const listHoldCleaningVoyages = async (
  query: HoldCleaningVoyageQuery,
  vesselsOnly = false,
): Promise<HoldCleaningVoyagePage> => {
  if (isHoldCleaningMockEnabled()) return mockListHoldCleaningVoyages(query, vesselsOnly);
  const response = await api.get<
    ApiEnvelope<{
      items: ApiVoyageSummary[];
      phaseCounts?: { all: number; planning: number; transit: number };
      informationCounts?: HoldCleaningVoyagePage["informationCounts"];
    }>
  >(`${HOLD_CLEANING_API}/${vesselsOnly ? "vessels" : "voyages"}`, {
    timeout: HOLD_CLEANING_READ_TIMEOUT_MS,
    params: {
      cursor: query.cursor || undefined,
      page: vesselsOnly || query.sortBy ? query.page : undefined,
      sortBy: query.sortBy,
      sortDirection: query.sortBy ? query.sortDirection ?? "asc" : undefined,
      limit: query.pageSize,
      imo: query.imo || undefined,
      status: query.status || undefined,
      search: query.search || undefined,
      dataCoverage: query.dataCoverage || undefined,
      information: vesselsOnly && query.information?.length ? query.information.join(",") : undefined,
      phase: query.phase || undefined,
    },
  });
  log("REPO - listHoldCleaningVoyages - response", { count: response.data.data.items.length, status: response.status });
  return {
    items: response.data.data.items.map((voyage) => toVoyageSummary(voyage, response.data.meta.datasetId)),
    phaseCounts: response.data.data.phaseCounts,
    informationCounts: response.data.data.informationCounts,
    page: query.page,
    pageSize: query.pageSize,
    nextCursor: response.data.meta.nextCursor ?? null,
    dataset: toDataset(response.data.meta),
  };
};

export const getHoldCleaningVoyage = async (voyageId: string, byImo = false): Promise<HoldCleaningVoyageDetail> => {
  if (isHoldCleaningMockEnabled()) return mockGetHoldCleaningVoyage(voyageId);
  const response = await api.get<ApiEnvelope<ApiVoyageDetail>>(
    `${HOLD_CLEANING_API}/${byImo ? "vessels" : "voyages"}/${encodePath(voyageId)}`,
  );
  log("REPO - getHoldCleaningVoyage - response", { status: response.status });
  const source = response.data.data;
  const forms = toForms(source.voyageId, source.formSummary, source.sourceLastUpdatedAt);
  const voyage = {
    ...toVoyageSummary(source, source.datasetId),
    loadPort: source.loadPort,
    dischargePort: source.dischargePort,
    formCount: forms.length,
  };
  const metaWarningKeys = new Set(response.data.meta.warnings.map(warningKey));
  return {
    voyage,
    dataset: toDataset({ ...response.data.meta, datasetId: source.datasetId }),
    crew: source.crewSummary ? toCrewSummary(source.crewSummary) : null,
    forms,
    lineage: [
      {
        source: "snowflake",
        sourceRecordId: source.sourceSnapshotId,
        version: response.data.meta.contractVersion,
        capturedAt: source.sourceLastUpdatedAt,
      },
    ],
    warnings: uniqueWarnings(source.warnings.filter((warning) => !metaWarningKeys.has(warningKey(warning)))),
    vesselFacts: source.vesselFacts,
    currentCleaning: source.currentCleaning,
    history: source.history,
  };
};

export const getHoldCleaningVesselHistory = async (imo: string): Promise<{ items: HoldCleaningVoyageSummary[] }> => {
  if (isHoldCleaningMockEnabled())
    return { items: (await listHoldCleaningVoyages({ imo, page: 1, pageSize: 5 })).items.slice(0, 5) };
  const response = await api.get<ApiEnvelope<{ items: ApiVoyageSummary[] }>>(
    `${HOLD_CLEANING_API}/vessels/${encodePath(imo)}/voyages`,
    { timeout: HOLD_CLEANING_READ_TIMEOUT_MS },
  );
  return { items: response.data.data.items.map((item) => toVoyageSummary(item, response.data.meta.datasetId)) };
};

export const getHoldCleaningReports = async (voyageId: string, cursor?: string) => {
  if (isHoldCleaningMockEnabled()) return { items: [], nextCursor: null };
  const response = await api.get<ApiEnvelope<{ items: HoldCleaningReport[] }>>(
    `${HOLD_CLEANING_API}/voyages/${encodePath(voyageId)}/reports`,
    { timeout: HOLD_CLEANING_READ_TIMEOUT_MS, params: { limit: 20, cursor } },
  );
  return { items: response.data.data.items, nextCursor: response.data.meta.nextCursor ?? null };
};

export const getHoldCleaningCrew = async (voyageId: string): Promise<HoldCleaningCrewSummary> => {
  if (isHoldCleaningMockEnabled()) return mockGetHoldCleaningCrew();
  const response = await api.get<ApiEnvelope<{ summary: ApiCrewSummary }>>(
    `${HOLD_CLEANING_API}/voyages/${encodePath(voyageId)}/crew`,
  );
  return toCrewSummary(response.data.data.summary);
};

export const getHoldCleaningImages = async (voyageId: string): Promise<HoldCleaningImage[]> => {
  if (isHoldCleaningMockEnabled()) return mockGetHoldCleaningImages();
  const items: ApiImage[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  const maximumPages = 20;

  for (let page = 0; page < maximumPages; page += 1) {
    const response = await api.get<ApiEnvelope<{ items: ApiImage[] }>>(
      `${HOLD_CLEANING_API}/voyages/${encodePath(voyageId)}/images`,
      { params: { cursor, limit: 100 } },
    );
    items.push(...response.data.data.items);
    const nextCursor = response.data.meta.nextCursor ?? null;
    if (!nextCursor) {
      cursor = undefined;
      break;
    }
    if (seenCursors.has(nextCursor)) {
      throw new Error("Image pagination returned a repeated cursor.");
    }
    seenCursors.add(nextCursor);
    cursor = nextCursor;
    if (page === maximumPages - 1) {
      throw new Error(`Image pagination exceeded the ${maximumPages * 100} image safety limit.`);
    }
  }

  log("REPO - getHoldCleaningImages - response", { count: items.length });
  return items.map((image) => ({
    category: image.category ?? "UNCLASSIFIED",
    categoryMode: image.categoryMode,
    imageId: image.imageId,
    holdNumber: image.holdNumber,
    mimeType: image.mediaType,
    capturedAt: image.createdAt,
    source: image.source === "MANUAL" ? "manual" : sourceMap[image.source],
    caption:
      image.caption ??
      (image.holdConfidence === null
        ? image.evidenceSource
        : `${image.evidenceSource ?? "Hold assignment"} (${Math.round(image.holdConfidence * 100)}% confidence)`),
  }));
};

export const createHoldCleaningManualEntry = async (
  voyageId: string,
  input: HoldCleaningManualEntryInput,
): Promise<void> => {
  if (isHoldCleaningMockEnabled()) return;
  await api.post(`${HOLD_CLEANING_API}/voyages/${encodePath(voyageId)}/manual-entries`, {
    ...input,
    idempotencyKey: createIdempotencyKey(),
  });
  log("REPO - createHoldCleaningManualEntry - response");
};

export const addHoldCleaningNote = async (voyageId: string, text: string): Promise<void> => {
  if (isHoldCleaningMockEnabled()) return;
  await api.post(`${HOLD_CLEANING_API}/voyages/${encodePath(voyageId)}/notes`, {
    text,
    idempotencyKey: createIdempotencyKey(),
  });
  log("REPO - addHoldCleaningNote - response");
};

export const uploadHoldCleaningPhotos = async (
  voyageId: string,
  files: readonly File[],
  holdNumber: number | null,
  caption: string,
): Promise<void> => {
  if (isHoldCleaningMockEnabled()) return;
  const form = new FormData();
  files.forEach((file) => form.append("files", file));
  if (holdNumber !== null) form.append("holdNumber", holdNumber.toString());
  if (caption.trim()) form.append("caption", caption.trim());
  form.append("idempotencyKey", createIdempotencyKey());
  await api.post(`${HOLD_CLEANING_API}/voyages/${encodePath(voyageId)}/images`, form);
  log("REPO - uploadHoldCleaningPhotos - response", { count: files.length });
};

export const downloadHoldCleaningRecord = async (voyageId: string): Promise<{ blob: Blob; filename: string }> => {
  if (isHoldCleaningMockEnabled()) {
    return {
      blob: new Blob(["Mock Hold Cleaning export"], { type: "application/pdf" }),
      filename: "hold-cleaning-mock.pdf",
    };
  }
  const response = await api.get(`${HOLD_CLEANING_API}/voyages/${encodePath(voyageId)}/export.pdf`, {
    responseType: "blob",
  });
  const disposition = String(response.headers["content-disposition"] ?? "");
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? "hold-cleaning-record.pdf";
  return { blob: response.data, filename };
};

export const getHoldCleaningImageSource = async (imageId: string): Promise<HoldCleaningImageSource> => {
  if (isHoldCleaningMockEnabled()) {
    return { url: URL.createObjectURL(mockGetHoldCleaningImageContent(imageId)), revokeOnDispose: true };
  }
  const response = await api.get<ApiEnvelope<{ url: string }>>(
    `${HOLD_CLEANING_API}/images/${encodePath(imageId)}/delivery-url`,
  );
  const url = response.data.data.url;
  if (new URL(url).protocol !== "https:") throw new Error("Hold Cleaning image URL must use HTTPS.");
  return { url, revokeOnDispose: false };
};

export const getHoldCleaningAssessment = async (voyageId: string): Promise<HoldCleaningAssessmentResult> => {
  if (isHoldCleaningMockEnabled()) return mockGetHoldCleaningAssessment(voyageId);
  const response = await api.get<ApiEnvelope<HoldCleaningAssessment>>(
    `${HOLD_CLEANING_API}/voyages/${encodePath(voyageId)}/assessment`,
  );
  return {
    assessment: response.data.data,
    etag: readEtag(response.headers as Record<string, unknown>),
  };
};

export const saveHoldCleaningAssessment = async (
  voyageId: string,
  payload: HoldCleaningAssessmentPayload,
  etag: string,
): Promise<HoldCleaningAssessmentResult> => {
  log("SERV - saveHoldCleaningAssessment");
  if (isHoldCleaningMockEnabled()) return mockSaveHoldCleaningAssessment(voyageId, payload);
  const response = await api.put<ApiEnvelope<HoldCleaningAssessment>>(
    `${HOLD_CLEANING_API}/voyages/${encodePath(voyageId)}/assessment`,
    payload,
    { headers: { "If-Match": etag } },
  );
  return {
    assessment: response.data.data,
    etag: readEtag(response.headers as Record<string, unknown>),
  };
};

export const generateHoldCleaningAssessment = async (
  voyageId: string,
  imageIds: string[],
  currentAssessmentRevision?: number,
): Promise<HoldCleaningAssessmentResult> => {
  log("SERV - generateHoldCleaningAssessment", { images: imageIds.length });
  if (isHoldCleaningMockEnabled()) {
    return mockGenerateHoldCleaningAssessment(voyageId, imageIds);
  }
  const response = await api.post<ApiEnvelope<HoldCleaningAssessment>>(
    `${HOLD_CLEANING_API}/voyages/${encodePath(voyageId)}/ai-assessment`,
    {
      promptContractVersion: AI_PROMPT_CONTRACT_VERSION,
      imageIds,
      currentAssessmentRevision,
      idempotencyKey: createIdempotencyKey(),
    },
  );
  return {
    assessment: response.data.data,
    etag: readEtag(response.headers as Record<string, unknown>),
  };
};

export const submitHoldCleaningAiFeedback = async (payload: HoldCleaningAiFeedbackPayload): Promise<void> => {
  if (isHoldCleaningMockEnabled()) return;
  await api.post(`${HOLD_CLEANING_API}/ai-feedback`, payload);
};

export const recordHoldCleaningAiUsage = async (
  eventName: string,
  voyageId: string,
  assessmentId?: string,
): Promise<void> => {
  if (isHoldCleaningMockEnabled()) return;
  await api.post(`${HOLD_CLEANING_API}/usage-events`, { eventName, voyageId, assessmentId });
};

const toSourceHealth = (source: ApiSourceHealth): HoldCleaningSourceHealth => ({
  source: sourceMap[source.source],
  state: !source.enabled
    ? "disabled"
    : source.status === "SUCCEEDED" && !source.stale
      ? "healthy"
      : source.status.includes("FAILED")
        ? "failed"
        : source.status === "PARTIAL" || source.stale
          ? "warning"
          : "unknown",
  lastSuccessfulAt: source.lastSuccessAt,
  lastAttemptedAt: source.lastAttemptAt,
  checkpointAt: source.sourceUpdatedAt,
  warning:
    source.lastErrorCode ??
    (source.stale ? "Source data is stale." : source.warningCount > 0 ? `${source.warningCount} warnings` : null),
});

export const getHoldCleaningSourceHealth = async (): Promise<HoldCleaningSourceHealth[]> => {
  if (isHoldCleaningMockEnabled()) return mockSourceHealth;
  const response = await api.get<ApiEnvelope<ApiSourceHealth[]>>(`${HOLD_CLEANING_API}/source-health`, {
    timeout: HOLD_CLEANING_READ_TIMEOUT_MS,
  });
  return response.data.data.map(toSourceHealth);
};

const toRefreshRun = (run: ApiRefreshRun): HoldCleaningRefreshRun => ({
  runId: run.runId,
  requestId: run.requestId,
  datasetId: run.datasetId,
  status: run.status,
  trigger: run.trigger,
  requestedAt: run.requestedAt,
  requestedBy: run.requestedBy,
  startedAt: run.startedAt,
  completedAt: run.completedAt,
  warningCount: run.sourceStatuses.reduce((sum, source) => sum + source.warningCount, 0),
  errorSummary:
    run.errorDetail ??
    (run.sourceStatuses
      .map((source) => source.lastErrorCode)
      .filter(Boolean)
      .join(", ") ||
      null),
  errorCode: run.errorCode,
  sourceStatuses: run.sourceStatuses.map(toSourceHealth),
  validationSummary: run.validationSummary,
});

export const listHoldCleaningRefreshRuns = async (
  page = 1,
  pageSize = 10,
  cursor?: string,
): Promise<HoldCleaningRunPage> => {
  if (isHoldCleaningMockEnabled()) return mockListHoldCleaningRefreshRuns(page, pageSize);
  const response = await api.get<ApiEnvelope<{ items: ApiRefreshRun[] }>>(`${HOLD_CLEANING_API}/refresh-runs`, {
    timeout: HOLD_CLEANING_READ_TIMEOUT_MS,
    params: { cursor, limit: pageSize },
  });
  return {
    items: response.data.data.items.map(toRefreshRun),
    page,
    pageSize,
    nextCursor: response.data.meta.nextCursor ?? null,
  };
};

export const getHoldCleaningRefreshRun = async (runId: string): Promise<HoldCleaningRefreshRun> => {
  if (isHoldCleaningMockEnabled()) return mockGetHoldCleaningRefreshRun(runId);
  const response = await api.get<ApiEnvelope<ApiRefreshRun>>(`${HOLD_CLEANING_API}/refresh-runs/${encodePath(runId)}`);
  return toRefreshRun(response.data.data);
};

export const getHoldCleaningRefreshStatus = async (): Promise<HoldCleaningRefreshStatus> => {
  if (isHoldCleaningMockEnabled()) return mockGetHoldCleaningRefreshStatus();
  const response = await api.get<ApiEnvelope<ApiRefreshStatus>>(`${HOLD_CLEANING_API}/refresh-status`);
  return {
    ...response.data.data,
    latestSuccess: response.data.data.latestSuccess ? toRefreshRun(response.data.data.latestSuccess) : null,
    latestFailure: response.data.data.latestFailure ? toRefreshRun(response.data.data.latestFailure) : null,
  };
};

export const requestHoldCleaningRefresh = async (): Promise<HoldCleaningRefreshRequest> => {
  if (isHoldCleaningMockEnabled()) return mockRequestHoldCleaningRefresh();
  const response = await api.post<ApiEnvelope<HoldCleaningRefreshRequest>>(`${HOLD_CLEANING_API}/refresh-requests`, {
    reason: "Manual refresh requested from Admin Debug",
    idempotencyKey: createIdempotencyKey(),
  });
  return response.data.data;
};

export const getHoldCleaningRefreshRequest = async (requestId: string): Promise<HoldCleaningRefreshRequest> => {
  if (isHoldCleaningMockEnabled()) return mockGetHoldCleaningRefreshRequest(requestId);
  const response = await api.get<ApiEnvelope<HoldCleaningRefreshRequest>>(
    `${HOLD_CLEANING_API}/refresh-requests/${encodePath(requestId)}`,
  );
  return response.data.data;
};

export const getHoldCleaningEmailIntelligenceStatus = async (): Promise<HoldCleaningEmailIntelligenceStatus> => {
  if (isHoldCleaningMockEnabled()) return mockGetHoldCleaningEmailIntelligenceStatus();
  const response = await api.get<ApiEnvelope<HoldCleaningEmailIntelligenceStatus>>(
    `${HOLD_CLEANING_API}/email-intelligence/status`,
  );
  return response.data.data;
};

export const requestHoldCleaningEmailIntelligenceBackfill = async (): Promise<HoldCleaningRefreshRequest> => {
  if (isHoldCleaningMockEnabled()) return mockRequestHoldCleaningEmailIntelligenceBackfill();
  const response = await api.post<ApiEnvelope<HoldCleaningRefreshRequest>>(
    `${HOLD_CLEANING_API}/email-intelligence/backfill`,
    {
      reason: "Historical email intelligence backfill requested from Admin Debug",
      idempotencyKey: createIdempotencyKey(),
    },
  );
  return response.data.data;
};

export const retryHoldCleaningEmailIntelligenceFailures = async (
  maxMessages: number,
  recoverUnavailable = false,
): Promise<HoldCleaningRefreshRequest> => {
  if (isHoldCleaningMockEnabled()) return mockRetryHoldCleaningEmailIntelligenceFailures();
  const response = await api.post<ApiEnvelope<HoldCleaningRefreshRequest>>(
    `${HOLD_CLEANING_API}/email-intelligence/retry-failures`,
    {
      reason: `${recoverUnavailable ? "Recover unavailable" : "Retry failed"} Hold Cleaning emails from Admin Debug (up to ${maxMessages.toString()})`,
      idempotencyKey: createIdempotencyKey(),
      maxMessages,
      ...(recoverUnavailable ? { recoverUnavailable: true } : {}),
    },
  );
  return response.data.data;
};

export const retryHoldCleaningRefreshRun = async (runId: string): Promise<HoldCleaningRefreshRequest> => {
  if (isHoldCleaningMockEnabled()) return mockRetryHoldCleaningRefreshRun();
  const response = await api.post<ApiEnvelope<HoldCleaningRefreshRequest>>(
    `${HOLD_CLEANING_API}/refresh-runs/${encodePath(runId)}/retry`,
    {
      reason: "Manual retry requested from Admin Debug",
      idempotencyKey: createIdempotencyKey(),
    },
  );
  return response.data.data;
};
